/**
 * Checks if a given value is "empty," similar to PHP's empty().
 *
 * A value is considered "empty" if:
 * - It is `null` or `undefined`
 * - It is an empty string (`''`)
 * - It is `false`
 * - It is `0` or `'0'` (string representation of zero)
 * - It is an empty array (`[]`) or all its elements are "empty"
 * - It is an empty object (`{}` with no properties) or all its property values are "empty"
 *
 * @param {*} value - The value to check for emptiness.
 * @returns {boolean} `true` if the value is empty, otherwise `false`.
 *
 * @example
 * isEmpty(''); // true (empty string)
 * isEmpty(null); // true (null)
 * isEmpty(false); // true (false)
 * isEmpty(0); // true (numeric zero)
 * isEmpty('0'); // true (string zero)
 * isEmpty([]); // true (empty array)
 * isEmpty(['', null, false]); // true (all elements are empty)
 * isEmpty(['a', '']); // false (array with a non-empty element)
 * isEmpty({}); // true (empty object)
 * isEmpty({ a: '', b: null }); // true (all properties are empty)
 * isEmpty({ a: 'hello' }); // false (object with a non-empty property)
 */
export function isEmpty(value) {
    // null または undefined
    if (value == null) {
        return true;
    }
    // 空文字
    if (value === '') {
        return true;
    }
    // false (厳密な判定)
    if (value === false) {
        return true;
    }
    // 0 または '0' (文字列のゼロ)
    if (value === 0 || value === '0') {
        return true;
    }
    // 空の配列 または 全ての要素が空
    if (Array.isArray(value)) {
        return value.length === 0 || value.every((item) => isEmpty(item));
    }
    // 空のオブジェクト または 全てのプロパティの値が空
    if (typeof value === 'object') {
        return Object.keys(value).length === 0 || Object.values(value).every((propValue) => isEmpty(propValue));
    }
    // その他は空ではない
    return false;
}
